import { createContext, useEffect, useState } from 'react';

import { formatUsername } from '../utilities/formatUsername';

const USER_STATE_LOGGED_IN = `USER_STATE_LOGGED_IN`;
const USER_STATE_NOT_LOGGED_IN = `USER_STATE_NOT_LOGGED_IN`;
const USER_STATE_PENDING = `USER_STATE_PENDING`;

const UserSessionContext = createContext({
	data: null,
	setUser: () => {},
});

// Derive the user object for state from a raw API payload, preserving any
// fields already present on `baseUser` (previously done by mutating state).
const genUserFromData = (baseUser, data) => {
	if (!data) {
		return null;
	}

	return {
		...baseUser,
		isMember: true,
		email: data.mail,
		username: data.name,
		hashedUserId: data.uuid,
		picture: data.picture?.url,
		name: formatUsername(data),
		memberFor: new Date() - parseInt(data.created),
	};
};

const getUserIdFromUserStore = () => {
	const userId = JSON.parse(localStorage.getItem('user_store'))?.state?.user.uid;
	if (userId) localStorage.setItem('userId', userId);

	return userId;
};

const UserSessionProvider = (props) => {
	const [user, setUser] = useState({ state: USER_STATE_PENDING });

	useEffect(() => {
		const getUser = async () => {
			// Read it from user_store if we're accessing GE from a Drupal link
			const userId = localStorage.getItem('userId') || getUserIdFromUserStore();
			if (!userId) {
				throw new Error(`Something went wrong in user API`);
			}

			const [userPromise, allGenomesPromise] = await Promise.allSettled([
				fetch(`/api/v1/user/${userId}`, { credentials: 'include' }),
				fetch(`/api/v1/genomes`, { credentials: 'include' }),
			]);

			let userData;
			let allGenomesData = {};

			if (userPromise.status === 'fulfilled' && userPromise.value.ok) {
				userData = await userPromise.value.json();
				if (!userData.error) {
					// Base is empty: at mount the only prior field is `state`,
					// which the caller below always overrides.
					userData = genUserFromData({}, { ...userData });
				}
			} else {
				return { error: true };
			}

			if (allGenomesPromise.status === 'fulfilled' && allGenomesPromise.value.ok) {
				allGenomesData = await allGenomesPromise.value.json();
				if (!allGenomesData.error) {
					// Prefer `genomes` (carries each genome's plan `membership`) over
					// `tabs` (title + uuid only). The genome plan is the source of
					// truth for the tier shown in the app bar; the explorer job's
					// subscription can be stale after a plan downgrade.
					allGenomesData = allGenomesData.genomes || allGenomesData.tabs;
				}
			}

			return {
				...userData,
				allGenomesData,
			};
		};

		getUser()
			.then((data) => {
				if (data.error) {
					setUser({
						state: USER_STATE_NOT_LOGGED_IN,
						referrer: document.referrer,
					});
					window.dataLayer?.push({ event: `start_pw` });
				} else {
					setUser({
						...data,
						state: USER_STATE_LOGGED_IN,
					});
					window.dataLayer?.push({
						event: `start_pw`,
						pw_user_email: data.email,
					});
				}
			})
			.catch(() => {
				setUser({
					state: USER_STATE_NOT_LOGGED_IN,
					referrer: document.referrer,
				});
				window.dataLayer?.push({ event: `start_pw` });
			});
	}, []);

	return (
		<UserSessionContext.Provider
			value={{
				data: user,
				setUser: (data) => setUser((prev) => genUserFromData(prev, data)),
			}}
		>
			{props.children}
		</UserSessionContext.Provider>
	);
};

export { USER_STATE_LOGGED_IN, USER_STATE_NOT_LOGGED_IN, USER_STATE_PENDING, UserSessionContext, UserSessionProvider };
